/**
 * InstallPrompt — surfaces the browser's PWA install prompt as a friendly
 * Snackbar with "Install" and "Dismiss" buttons. Snoozes for 14 days when
 * dismissed so we don't pester the user every visit.
 */
import React, { useEffect, useState } from 'react';
import { Snackbar, Box, Button, Typography } from '@mui/material';
import GetAppIcon from '@mui/icons-material/GetApp';
import CloseIcon from '@mui/icons-material/Close';

const DISMISS_KEY = 'pwa.installDismissedAt.v1';
const SNOOZE_MS = 14 * 24 * 60 * 60 * 1000;

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

const InstallPrompt: React.FC = () => {
  const [deferred, setDeferred] = useState<BeforeInstallPromptEvent | null>(null);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onBeforeInstall = (e: Event) => {
      // Don't show if recently dismissed
      try {
        const last = parseInt(localStorage.getItem(DISMISS_KEY) || '0', 10);
        if (last && Date.now() - last < SNOOZE_MS) return;
      } catch { /* ignore */ }

      e.preventDefault();
      setDeferred(e as BeforeInstallPromptEvent);
      setOpen(true);
    };
    window.addEventListener('beforeinstallprompt', onBeforeInstall);
    return () => window.removeEventListener('beforeinstallprompt', onBeforeInstall);
  }, []);

  const handleInstall = async () => {
    if (!deferred) return;
    try {
      await deferred.prompt();
      const choice = await deferred.userChoice;
      if (choice.outcome === 'dismissed') {
        try { localStorage.setItem(DISMISS_KEY, String(Date.now())); } catch { /* ignore */ }
      }
    } catch { /* ignore — user may have closed the OS dialog */ }
    setDeferred(null);
    setOpen(false);
  };

  const handleDismiss = () => {
    try { localStorage.setItem(DISMISS_KEY, String(Date.now())); } catch { /* ignore */ }
    setOpen(false);
  };

  return (
    <Snackbar
      open={open}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      onClose={(_, reason) => { if (reason === 'clickaway') return; setOpen(false); }}
    >
      <Box sx={{
        display: 'flex', alignItems: 'center', gap: 1.5,
        bgcolor: 'background.paper', border: '1px solid', borderColor: 'divider',
        boxShadow: 4, borderRadius: 1.5, p: 1.5, maxWidth: 380,
      }}>
        <GetAppIcon color="primary" />
        <Box sx={{ flex: 1 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 700, lineHeight: 1.1 }}>
            Install earngush?
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Put it on your home screen for one-tap access — no app store needed.
          </Typography>
        </Box>
        <Button size="small" variant="contained" onClick={handleInstall}>Install</Button>
        <Button size="small" onClick={handleDismiss} sx={{ minWidth: 0, p: 0.5 }} aria-label="Dismiss">
          <CloseIcon fontSize="small" />
        </Button>
      </Box>
    </Snackbar>
  );
};

export default InstallPrompt;
